<template>
  <div v-if="value.type">
    <header class="flex items-center mb-6">
      <lm-icon :icon="value.type.toLowerCase()" class="mr-3 text-gray-700" />
      <h1 class="text-gray-700">{{ FeedSourcesLabels[value.type] }}</h1>
    </header>
    <acceso-list
      v-if="isAcceso"
      ref="inputAcceso"
      @feed-selected="accesoFeed = $event"
    />
    <channel-list
      v-else-if="isChannel"
      ref="inputChannel"
      @channel-selected="channelFeed = $event"
    />
    <pipe-list
      v-else-if="isPipe"
      ref="inputPipes"
      @pipeSelected="pipeFeed = $event"
    />
    <lm-input
      v-else-if="isStandard"
      ref="inputUrl"
      :label="inputLabelFromSource"
      :help="inputHelpFromSource"
      focus
      :maxlength="0"
      @submit="$emit(Events.NEXT_STEP)"
    />
  </div>
</template>

<script>
import LmInput from '@/components/form/LmInput'
import AccesoList from '@/components/modals/feedSources/AccesoList'
import ChannelList from '@/components/modals/feedSources/ChannelList'
import PipeList from '@/components/modals/feedSources/PipeList'
import LmIcon from '@/components/ui/LmIcon'
import {Events, FeedSources, FeedSourcesLabels} from '@/lib/definitions'
const STANDARD_FEED_TYPES = [
  FeedSources.RSS,
  FeedSources.YOUTUBE,
  FeedSources.VIMEO,
  FeedSources.TWITTER,
  FeedSources.LMSEARCH,
  FeedSources.MEDIUM,
  FeedSources.SLIDESHARE,
  FeedSources.REDDIT,
  FeedSources.TELEGRAM,
  FeedSources.NEWSLETTER
]
export default {
  name: 'FeedSourceStep2',
  components: {
    PipeList,
    ChannelList,
    AccesoList,
    LmIcon,
    LmInput
  },
  props: {
    value: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      Events,
      FeedSourcesLabels,
      accesoFeed: null,
      channelFeed: null,
      pipeFeed: null
    }
  },
  computed: {
    inputLabelFromSource() {
      switch (this.value.type) {
        case FeedSources.RSS:
          return 'RSS Feed URL'
        case FeedSources.YOUTUBE:
          return 'Youtube URL (user, channel or playlist)'
        case FeedSources.VIMEO:
          return 'Vimeo Username or Vimeo URL (user or channel)'
        case FeedSources.TWITTER:
          return 'Twitter handle'
        case FeedSources.LMSEARCH:
          return 'Text to search'
        case FeedSources.MEDIUM:
          return 'Medium user or publications URL'
        case FeedSources.SLIDESHARE:
          return 'SlideShare user name or URL'
        case FeedSources.REDDIT:
          return 'Subreddit name or URL'
        case FeedSources.TELEGRAM:
          return 'Telegram channel name or URL'
        case FeedSources.NEWSLETTER:
          return 'Newsletter URL'
        case FeedSources.CHANNEL:
          return 'Title'
      }
      return ''
    },
    inputHelpFromSource() {
      switch (this.value.type) {
        case FeedSources.VIMEO:
          return 'Ex. landscapemonitor, https://vimeo.com/landscapemonitor, https://vimeo.com/channels/staffpicks'
      }
      return ''
    },
    isStandard() {
      return STANDARD_FEED_TYPES.includes(this.value.type)
    },
    isAcceso() {
      return this.value.type === FeedSources.ACCESO
    },
    isChannel() {
      return this.value.type === FeedSources.CHANNEL
    },
    isPipe() {
      return this.value.type === FeedSources.PIPE
    }
  },
  methods: {
    async init() {
      if (this.isStandard) {
        const input = this.$refs.inputUrl
        const source = this.value.source
        if (source) input.setData(source)
        await this.$nextTick()
        input.setFocus()
      }
    },
    validate() {
      return new Promise((resolve, reject) => {
        const payload = {
          type: this.value.type,
          description: ''
        }
        if (this.isAcceso && this.accesoFeed) {
          payload.source = this.accesoFeed.source
          payload.title = this.accesoFeed.title
        } else if (this.isChannel && this.channelFeed) {
          payload.source = this.channelFeed.url
          payload.title = this.channelFeed.title
          payload.description = this.channelFeed.description
        } else if (this.isPipe && this.pipeFeed) {
          payload.source = {
            pipeId: this.pipeFeed.pipeId,
            subPipeId: this.pipeFeed.subPipeId
          }
          payload.title = this.pipeFeed.title
          payload.description = this.pipeFeed.description
        } else if (this.isStandard) {
          payload.source = this.$refs.inputUrl.data
        }
        if (!payload.source) {
          this.showUrlError()
          return reject()
        }
        if (this.isAcceso || this.isChannel || this.isPipe) {
          this.$emit('input', {
            ...this.value,
            source: payload.source,
            title: payload.title,
            description: payload.description
          })
          resolve()
        } else if (this.isStandard) {
          this.$store
            .dispatch('checkSource', payload)
            .then(source => {
              this.$emit('input', {
                ...this.value,
                source: payload.source,
                url: source.url || null,
                title: source.title,
                description: source.description ? source.description : ''
              })
              resolve()
            })
            .catch(e => {
              this.showUrlError(e)
              reject()
            })
        }
      })
    },
    showUrlError(error) {
      if (this.isAcceso) {
        this.$refs.inputAcceso.error = true
      } else if (this.isChannel) {
        this.$refs.inputChannel.error = true
      } else if (this.isPipe) {
        this.$refs.inputPipes.error = true
      } else if (this.isStandard) {
        this.$refs.inputUrl.error = true
        this.$refs.inputUrl.errorMessage = 'Please fill in this field'
        if (error && (error.devCode === 404 || error.devCode === 400)) {
          this.$refs.inputUrl.errorMessage = error.devMessage
        }
      }
    },
    clear() {
      if (this.isAcceso) {
        this.accesoFeed = null
        this.$refs.inputAcceso.reset()
      } else if (this.isChannel) {
        this.channelFeed = null
        this.$refs.inputChannel.reset()
      } else if (this.isPipe) {
        this.$refs.inputPipes.reset()
      } else {
        this.$refs.inputUrl.reset()
      }
    }
  }
}
</script>

<style scoped></style>
